import { PERMISSIONS, type Permission } from '@repo/shared-types';
import { type ReactElement, type ReactNode, useMemo } from 'react';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Pure check — does the provided permission list include the required one?
 *
 * The AuthUser shape attaches permissions as a precomputed readonly
 * array (one per role — see `@repo/domain`'s ROLE_PERMISSIONS). This is
 * a hot path: the sidebar renders `useMemo(navItems.filter(hasPermission(...)))`
 * on every render, so we keep the function allocation-free and order
 * the cheap guard before the array scan.
 */
export function hasPermission(
  userPermissions: readonly Permission[],
  required: Permission,
): boolean {
  if (!userPermissions || userPermissions.length === 0) return false;
  return userPermissions.includes(required);
}

/**
 * React hook — read the current user's permissions from the auth
 * store and check a required one. Returns `false` while the store
 * is still hydrating so a flash of "no permission" does not redirect
 * before the FIRST signin completes.
 */
export function useHasPermission(required: Permission): boolean {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s._hasHydrated);
  return useMemo(() => {
    if (!hydrated || !user) return false;
    return hasPermission(user.permissions, required);
  }, [hydrated, user, required]);
}

export interface PermissionGateProps {
  permission: Permission | readonly Permission[];
  /** When multiple permissions are passed, ALL must be present. */
  mode?: 'all' | 'any';
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Declarative UI gate — renders `children` only if the current user
 * has the required permission(s). Use for buttons/menu items that
 * should not appear at all for users without the right.
 *
 * For data-fetch gating (do not fetch unless allowed) use
 * `useHasPermission()` in the feature hook instead.
 */
export function PermissionGate({
  permission,
  mode = 'all',
  children,
  fallback = null,
}: PermissionGateProps): ReactElement | null {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s._hasHydrated);

  const visible = useMemo(() => {
    if (!hydrated || !user) return false;
    if (Array.isArray(permission)) {
      if (permission.length === 0) return true;
      return mode === 'all'
        ? permission.every((p) => hasPermission(user.permissions, p))
        : permission.some((p) => hasPermission(user.permissions, p));
    }
    return hasPermission(user.permissions, permission as Permission);
  }, [hydrated, user, permission, mode]);

  if (!visible) return fallback as ReactElement | null;
  return children as ReactElement;
}

/**
 * Convenience aggregation of the permission catalog for use in the
 * TenantSwitcher / Sidebar nav config — re-exports the PERMISSIONS
 * tree from shared-types so consuming files do not need a second
 * import statement for the constant.
 */
export const PERMS = PERMISSIONS;
